import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Patient } from '@app/shared/models';
import { ConsentService } from '@app/core/patients/consents/services/consent.service';
import { FilesService } from '@app/shared/services/api/files.service';
import { PrintService } from '@app/shared/services/helpers/print.service';
import { firstValueFrom, forkJoin, of, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

/**
 * Informed consent print (letter template):
 * - Rich text: body HTML + acknowledgments + signatures via browser print.
 * - PDF template: server-built merged PDF (consent_json.merged_pdf_path) opens in a new tab;
 *   {@link PrintService.print} is skipped so users print from the PDF viewer at actual size.
 */
@Component({
    selector: 'app-print-informed-consent',
    templateUrl: './print-informed-consent.component.html',
    styleUrls: ['./print-informed-consent.component.scss']
})
export class PrintInformedConsentComponent implements OnInit, OnDestroy {

    patient: Patient;
    assessment: any;
    assessJSON: any;
    assessJSONHTML: SafeHtml;
    assessmentItems: any;
    answers: any[] = [];
    isPdfMode: boolean = false;

    printSub: Subscription;
    patientSub: Subscription;

    signatureUrl: string = null;
    guardianSignatureUrl: string = null;

    private signaturesReady = false;
    private hasCompletedPrint = false;
    private isDestroyed = false;

    private pdfDataUrlForActions: string | null = null;
    /** From {@link ConsentService.ensureMergedPdf} — presigned URL; opens in a new tab without a second fetch. */
    private mergedPdfRemoteUrl: string | null = null;
    pdfActionsLoading = false;
    pdfActionsError: string | null = null;

    constructor(
        public printSvc: PrintService,
        public filesService: FilesService,
        public sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
        private consentSvc: ConsentService
    ) { }

    ngOnInit() {
        this.printSvc.contentReady = false;
        this.printSvc.hideSignatures.next(true);
        this.patientSub = this.printSvc.activePatient.subscribe(
            (patient: any) => {
                this.patient = patient;
            }
        );
        this.printSub = this.printSvc.toPrintData.subscribe(
            (printData: any) => {
                if (this.isDestroyed) {
                    return;
                }
                if (printData && printData.assessment) {
                    this.resetPdfActions();
                    this.hasCompletedPrint = false;
                    this.signaturesReady = false;
                    this.assessment = printData.assessment;
                    this.assessJSON = this.parseConsentJson(this.assessment.consentJson);
                    this.assessJSONHTML = this.sanitizeHtml(this.assessJSON.text || '');
                    this.answers = Array.isArray(this.assessJSON.answers) ? this.assessJSON.answers : [];
                    this.isPdfMode = this.isPdfTemplateConsent(this.assessJSON);

                    const mergePdf$ =
                        this.isPdfMode && this.assessment?.id
                            ? this.consentSvc.ensureMergedPdf(this.assessment.id).pipe(
                                  tap((resp: any) => {
                                      const u =
                                          resp &&
                                          typeof resp.mergedPdfTemporaryUrl === 'string' &&
                                          resp.mergedPdfTemporaryUrl.trim();
                                      this.mergedPdfRemoteUrl = u ? resp.mergedPdfTemporaryUrl.trim() : null;
                                      const row = this.unwrapConsentResourceRow(resp);
                                      if (row?.consentJson != null) {
                                          this.assessment.consentJson =
                                              typeof row.consentJson === 'string'
                                                  ? row.consentJson
                                                  : JSON.stringify(row.consentJson);
                                          this.assessJSON = this.parseConsentJson(this.assessment.consentJson);
                                          this.isPdfMode = this.isPdfTemplateConsent(this.assessJSON);
                                          this.answers = Array.isArray(this.assessJSON.answers)
                                              ? this.assessJSON.answers
                                              : [];
                                      }
                                  }),
                                  catchError(() => of(null))
                              )
                            : of(null);

                    const signatureObservable = this.assessment.signatureUrl
                        ? this.filesService.getFilenameDataUri(this.assessment.signatureUrl).pipe(
                              catchError(() => of(null))
                          )
                        : of(null);
                    const guardianSignatureObservable = this.assessment.guardianSignatureUrl
                        ? this.filesService.getFilenameDataUri(this.assessment.guardianSignatureUrl).pipe(
                              catchError(() => of(null))
                          )
                        : of(null);

                    forkJoin({
                        merged: mergePdf$,
                        signatureData: signatureObservable,
                        guardianSignatureData: guardianSignatureObservable
                    }).subscribe((results: any) => {
                        if (this.isDestroyed) {
                            return;
                        }
                        this.signatureUrl = results.signatureData?.uri;
                        this.guardianSignatureUrl = results.guardianSignatureData?.uri;

                        const sigUrls = [this.signatureUrl, this.guardianSignatureUrl].filter(
                            (u): u is string => typeof u === 'string' && !!u
                        );

                        this.preloadSignatureImagesPromise(sigUrls).then(() => {
                            if (this.isDestroyed) {
                                return;
                            }
                            this.signaturesReady = true;
                            this.cdr.markForCheck();
                            this.tryFinishPrint();
                        });
                    });
                }
            }
        );
    }

    private resetPdfActions() {
        this.pdfDataUrlForActions = null;
        this.mergedPdfRemoteUrl = null;
        this.pdfActionsLoading = false;
        this.pdfActionsError = null;
    }

    ngOnDestroy() {
        this.isDestroyed = true;
        this.printSub.unsubscribe();
        this.patientSub.unsubscribe();
    }

    /** True when this consent uses an uploaded PDF template (storage key in consent_json). */
    private isPdfTemplateConsent(j: any): boolean {
        if (!j) {
            return false;
        }
        if (j.template_mode === 'pdf' || j.templateMode === 'pdf') {
            return true;
        }
        const p = j.pdf_path ?? j.pdfPath;
        return !!(p && String(p).trim());
    }

    /** Laravel JsonResource and interceptors may nest the consent under `data` once or twice. */
    private unwrapConsentResourceRow(resp: any): any {
        if (!resp || typeof resp !== 'object') {
            return null;
        }
        if (resp.consentJson != null) {
            return resp;
        }
        if (resp.data?.consentJson != null) {
            return resp.data;
        }
        if (resp.data?.data?.consentJson != null) {
            return resp.data.data;
        }
        return resp.data ?? resp;
    }

    private parseConsentJson(consentJson: unknown): any {
        let raw: any;
        if (typeof consentJson === 'object' && consentJson !== null) {
            raw = consentJson as any;
        } else if (typeof consentJson === 'string' && consentJson.trim()) {
            try {
                raw = JSON.parse(consentJson);
            } catch (e) {
                console.error('consentJson parse failed', e);
                raw = {};
            }
        } else {
            raw = {};
        }
        const mode = raw.template_mode ?? raw.templateMode ?? 'html';
        return {
            ...raw,
            template_mode: mode,
            pdf_path: raw.pdf_path ?? raw.pdfPath,
            merged_pdf_path: raw.merged_pdf_path ?? raw.mergedPdfPath
        };
    }

    print() {
        if (this.isDestroyed) {
            return;
        }
        if (this.hasCompletedPrint) {
            return;
        }
        if (!this.signaturesReady) {
            return;
        }

        if (this.isPdfMode && this.getPdfStorageKey()) {
            this.printSvc.skipBrowserPrintOnce = true;
            void this.openOriginalPdfInNewTab();
        }

        this.hasCompletedPrint = true;
        this.printSvc.contentReady = true;
    }

    private tryFinishPrint() {
        if (this.isDestroyed) {
            return;
        }
        if (this.hasCompletedPrint) {
            return;
        }
        if (!this.signaturesReady) {
            return;
        }
        setTimeout(() => {
            if (!this.isDestroyed) {
                this.print();
            }
        }, 100);
    }

    sanitizeHtml(parseJson: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(parseJson);
    }

    private getPdfStorageKey(): string {
        const mergedPath = this.assessJSON?.merged_pdf_path;
        const merged = mergedPath != null && String(mergedPath).trim();
        if (merged) {
            return String(mergedPath).trim();
        }
        const tplPath = this.assessJSON?.pdf_path;
        const tpl = tplPath != null && String(tplPath).trim();
        return tpl ? String(tplPath).trim() : '';
    }

    private getSuggestedPdfFilename(): string {
        const raw = (this.assessment?.consentName || 'consent').toString();
        const s = raw.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') || 'consent';
        const suffix = this.assessJSON?.merged_pdf_path ? '-full' : '';
        return `${s}${suffix}.pdf`;
    }

    /**
     * Build a PDF Blob from a data: URL. Avoids fetch(dataUrl) mime quirks and keeps type explicit for save/open.
     */
    private dataUrlToPdfBlob(dataUrl: string): Blob {
        const comma = dataUrl.indexOf(',');
        if (comma === -1) {
            throw new Error('Invalid data URL');
        }
        const data = dataUrl.slice(comma + 1);
        const isBase64 = /;base64/i.test(dataUrl.slice(0, comma));
        if (isBase64) {
            const binary = atob(data);
            const len = binary.length;
            const buffer = new ArrayBuffer(len);
            const view = new Uint8Array(buffer);
            for (let i = 0; i < len; i++) {
                view[i] = binary.charCodeAt(i);
            }
            return new Blob([buffer], { type: 'application/pdf' });
        }
        const encoded = new TextEncoder().encode(decodeURIComponent(data));
        return new Blob([encoded.buffer], { type: 'application/pdf' });
    }

    private triggerBlobDownload(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Revoking immediately can cancel the download in some browsers.
        setTimeout(() => URL.revokeObjectURL(url), 120000);
    }

    private async loadOriginalPdfDataUrlIfNeeded(): Promise<string | null> {
        if (this.pdfDataUrlForActions) {
            return this.pdfDataUrlForActions;
        }
        const key = this.getPdfStorageKey();
        if (!key) {
            return null;
        }
        this.pdfActionsLoading = true;
        this.pdfActionsError = null;
        this.cdr.markForCheck();
        try {
            const u = await firstValueFrom(
                this.filesService.getPatientFileDataUrlForConsentPrint(key)
            );
            if (this.isDestroyed) {
                return null;
            }
            if (!u) {
                this.pdfActionsError = 'Could not load the PDF. Try again or contact support.';
                this.pdfActionsLoading = false;
                this.cdr.markForCheck();
                return null;
            }
            this.pdfDataUrlForActions = u;
            this.pdfActionsLoading = false;
            this.cdr.markForCheck();
            return u;
        } catch (e) {
            if (!this.isDestroyed) {
                this.pdfActionsError = 'Could not load the PDF. Try again or contact support.';
                this.pdfActionsLoading = false;
                this.cdr.markForCheck();
            }
            return null;
        }
    }

    async downloadOriginalPdf(): Promise<void> {
        const dataUrl = await this.loadOriginalPdfDataUrlIfNeeded();
        if (this.isDestroyed) {
            return;
        }
        if (!dataUrl) {
            if (!this.pdfActionsError) {
                this.pdfActionsError = 'No PDF data available.';
                this.cdr.markForCheck();
            }
            return;
        }
        try {
            const blob = this.dataUrlToPdfBlob(dataUrl);
            this.triggerBlobDownload(blob, this.getSuggestedPdfFilename());
        } catch (e) {
            console.error('downloadOriginalPdf', e);
            this.pdfActionsError = 'Download failed.';
            this.cdr.markForCheck();
        }
    }

    async openOriginalPdfInNewTab(): Promise<void> {
        if (this.mergedPdfRemoteUrl) {
            const w = window.open(this.mergedPdfRemoteUrl, '_blank', 'noopener,noreferrer');
            if (!w) {
                this.pdfActionsError = 'Popup blocked. Allow popups for this site or use Download.';
                this.cdr.markForCheck();
            }
            return;
        }
        const dataUrl = await this.loadOriginalPdfDataUrlIfNeeded();
        if (this.isDestroyed) {
            return;
        }
        if (!dataUrl) {
            if (!this.pdfActionsError) {
                this.pdfActionsError = 'No PDF data available.';
                this.cdr.markForCheck();
            }
            return;
        }
        try {
            const blob = this.dataUrlToPdfBlob(dataUrl);
            const objectUrl = URL.createObjectURL(blob);
            const w = window.open(objectUrl, '_blank', 'noopener,noreferrer');
            if (!w) {
                this.pdfActionsError = 'Popup blocked. Allow popups for this site or use Download.';
                this.cdr.markForCheck();
            }
            // Tab URL will show as blob:… until closed — that is normal for in-memory PDFs.
            setTimeout(() => URL.revokeObjectURL(objectUrl), 300000);
        } catch (e) {
            console.error('openOriginalPdfInNewTab', e);
            this.pdfActionsError = 'Could not open the PDF.';
            this.cdr.markForCheck();
        }
    }

    private preloadSignatureImagesPromise(urls: string[]): Promise<void> {
        if (!urls || urls.length === 0) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            let pending = urls.length;
            const done = () => {
                pending -= 1;
                if (pending <= 0) {
                    resolve();
                }
            };
            urls.forEach((src) => {
                const img = new Image();
                img.onload = () => done();
                img.onerror = () => done();
                img.src = src;
            });
        });
    }
}
